import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { Input, Spacer, VDBText } from "..";
import {
  View,
  FlatList,
  Dimensions,
  LayoutRectangle,
  LayoutChangeEvent,
} from "react-native";
import { useState, useRef, useEffect, SetStateAction } from "react";
import { VDBIcons } from "@utils/VDBIcons";
import styled from "styled-components/native";
import LineViewContainer from "@scenes/requests/utils/LineView";
import { SpaceType } from "../Spacer/types";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";
import { Controller, useForm } from "react-hook-form";
//import { useTranslation } from "react-i18next";
import { useSearchFormData } from "@apis/dynamicFormApi";
import { useDebounce } from "@components/molecules/DynamicForm/utils";
import { SelectedItemTheme } from "../Button/types";
import Routes from "@navigations/Routes";
import { navigate } from "@navigations/utils/RootNavigation";
import { getStyleForSearchCategory } from "../Filters/utils";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useEnablePersonalisationFont } from "@redux/slices/globalSlice";
import React from "react";
import { DefaultTheme } from "styled-components/native";
import useEngraveFonts from "../../../hooks/useEngraveFonts";

export type CustomSelectOption = {
  label: string;
  value: string;
  isDisabled?: boolean;
  /** Remote font URL (S3/CDN) - if provided, loads font from this URL instead of local bundle */
  fontUrl?: string;
};

export type CustomSelectProps = {
  title: string;
  options: CustomSelectOption[];
  onSelect: (value: string) => void;
  defaultValue?: string;
  customWidth?: number;
  position?: "left" | "right";
  field?: any;
  setSingleRequestFormValue?: any;
  setFormValue?: any;
  inputNames?: any[];
  error?: string;
  showModal?: (
    modalType:
      | "delete-total-price"
      | "reset-single-form"
      | "replace-email"
      | null,
    callback: () => void
  ) => void;
  dynamicFormValues?: any;
  productType?: string;
  disabled?: boolean;
  showAsPopover?: boolean;
  /** When true, renders each option label using its value as font family (for font selection dropdowns) */
  useFontStyle?: boolean;
  /** Show error state on the dropdown */
  showError?: boolean;
  /** Error message to display */
  errorMessage?: string;
};

const CustomSelect = ({
  options,
  defaultValue,
  onSelect,
  title,
  field,
  setSingleRequestFormValue,
  setFormValue,
  inputNames,
  error,
  showModal,
  dynamicFormValues,
  productType,
  disabled = false,
  useFontStyle = false,
  showError = false,
  errorMessage,
}: CustomSelectProps) => {
  const { theme, typography, t } = useScreenOptionsHooks();
  const { user } = useUserInfo();
  const enablePersonalisationFont = useEnablePersonalisationFont();

  // Font styling is only enabled if both the prop is true AND the global switch is on
  const isFontStyleEnabled = useFontStyle && enablePersonalisationFont;

  const { loadFontsWithUrls, getFontFamilyForStyle } = useEngraveFonts();

  const selectedStyle: SelectedItemTheme = getStyleForSearchCategory(
    productType ?? "",
    user?.custom_theme ?? false
  );

  const shareType =
    dynamicFormValues?.requestForm.radioSelect === "whatsApp"
      ? "share_via_whatsapp"
      : "share_via_email";
  const triggerRef = useRef<View>(null);
  const [selectedValue, setSelectedValue] = useState(defaultValue);
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [apiData, setApiData] = useState(null);

  const { control, setValue } = useForm({
    defaultValues: {
      searchQueryFormValue: "",
    },
  });
  const [layout, setLayout] = useState(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const { data } = useSearchFormData(
    !!debouncedSearchQuery && !!field?.api,
    field?.api,
    debouncedSearchQuery,
    field?.query_key,
    shareType
  );

  const Wrapper = !theme.isLargeScreen ? TouchableContainer : View;

  useEffect(() => {
    if (field?.api) {
      if (data?.response?.body?.length < 1) {
        setDropdownVisible(false);
      }
      setApiData(data?.response?.body);
    }
  }, [data?.response?.body]);

  const handleSelect = (
    value: string,
    item?: any,
    needUpdateValue?: boolean
  ) => {
    const handleSelectFunc = () => {
      onSelect(value);
      setSelectedValue(value);
      setDropdownVisible(false);
      if (item?.account_id) {
        setSingleRequestFormValue("account_id", item.account_id);
      }
      if (item.recipient_id || needUpdateValue) {
        updateFormValues(item);
      }
    };
    const handleApiFunc = () => {
      handleSelectFunc();
      setValue("searchQueryFormValue", selectedValue ?? "");
      setSingleRequestFormValue("recipientId", item.recipient_id);
      setFormValue("recipientId", item.recipient_id);
      setFormValue("recipient_company_name", item?.recipient_company_name);
      setFormValue(
        "recipient_company_location",
        item?.recipient_company_location
      );
      onSelect(value);
    };
    if (field?.api) {
      if (
        dynamicFormValues?.requestForm[field.name] &&
        dynamicFormValues?.requestForm.radioSelect !== "whatsApp"
      ) {
        const recipientEmail = dynamicFormValues?.requestForm?.recipientEmail;

        const recipientName = dynamicFormValues?.requestForm?.recipientName;

        if (field.name === "recipientName") {
          if (recipientEmail && recipientEmail.length > 0) {
            showModal?.(field.name, () => {
              handleApiFunc();
            });
          } else {
            handleApiFunc();
          }
        } else if (field.name === "recipientEmail") {
          if (recipientName && recipientName.length > 0) {
            showModal?.(field.name, () => {
              handleApiFunc();
            });
          } else {
            handleApiFunc();
          }
        } else {
          showModal?.(field.name, () => {
            handleApiFunc();
          });
        }
      } else {
        handleApiFunc();
      }
    } else {
      handleSelectFunc();
    }
  };
  const updateFormValues = (item: any) => {
    inputNames?.forEach((name) => {
      if (field?.associatedInput && field?.associatedInput.includes(name)) {
        const valueKey = field?.associatedValue?.[name];
        setSingleRequestFormValue(name, item?.[valueKey]);
        setFormValue(name, item?.[valueKey]);
      }
    });
  };

  const openFilterSearchScreen = () => {
    const props = {
      selectionType: "single",
      title: field.label,
      customIcon: "",
      payloadName: "",
      api: field?.api || "",
      server_side_search: false,
      selectedStyle: selectedStyle,
      api_query_key: field?.query_key,
      name: field.name,
      inputLabel: field.label,
      shareType: shareType,
      dynamicFormValues: dynamicFormValues,
      productType: productType,
      initial: false,
      onSelectItem: (selectedItem: any) => {
        setValue("searchQueryFormValue", selectedItem.value);
        handleSelect(
          selectedItem?.value,
          selectedItem,
          selectedItem.phone_number ? true : false
        );
      },
    };
    if (!theme.isLargeScreen) {
      navigate(Routes.DropDownSearchScreen, props);
    }
  };

  const openDropdown = () => {
    if (layout) {
      setDropdownPosition({
        top:
          Math.floor((layout as LayoutRectangle)?.y) +
          Math.floor((layout as LayoutRectangle)?.height),
        left: Math.floor((layout as LayoutRectangle)?.x),
        width: Math.floor((layout as LayoutRectangle)?.width),
      });
      setDropdownVisible(!isDropdownVisible);

      if (isFontStyleEnabled && options.length > 0 && !isDropdownVisible) {
        loadFontsWithUrls(options);
      }
    }
  };

  useEffect(() => {
    if (apiData && !isDropdownVisible) {
      openDropdown();
    }
  }, [apiData]);

  useEffect(() => {
    setSearchQuery("");
    setApiData(null);
    setDropdownVisible(false);
  }, [dynamicFormValues?.requestForm.radioSelect]);

  useEffect(() => {
    setValue("searchQueryFormValue", defaultValue ?? "");
    setSelectedValue(defaultValue);
    if (selectedValue) {
    }
  }, [defaultValue]);

  return (
    <Container>
      {
        <>
          {!field?.api ? (
            <>
              <TriggerButton
                onPress={openDropdown}
                theme={theme}
                onLayout={(event: LayoutChangeEvent) => {
                  setLayout(
                    event?.nativeEvent
                      ?.layout as unknown as SetStateAction<null>
                  );
                }}
                disabled={disabled}
                hasError={showError}
              >
                <View
                  ref={triggerRef}
                  style={{
                    flexDirection: "column",
                    gap: 4,
                    flexShrink: 1,
                    flexGrow: 1,
                  }}
                >
                  <VDBText style={{ padding: 0, margin: 0 }}>{title}</VDBText>
                  <VDBText
                    color={disabled ? theme["core-04"] : theme["core-05"]}
                    type={
                      isFontStyleEnabled
                        ? {
                            ...typography["Body-M"],
                            fontFamily: getFontFamilyForStyle(
                              selectedValue ?? ""
                            ),
                          }
                        : typography["Body-M"]
                    }
                  >
                    {options.find((option) => option.value === selectedValue)
                      ?.label ||
                      t("bulkInitScreen.selectOption.text", "Select Option")}
                  </VDBText>
                </View>
                <VDBIcon
                  size={24}
                  color={disabled ? theme["core-04"] : theme["main-01"]}
                  variant={IconsVariant.VDBCustom}
                  name={
                    isDropdownVisible
                      ? VDBIcons.FILLED_CIRCLE_UP
                      : VDBIcons.FILLED_CIRCLE_DOWN
                  }
                />
              </TriggerButton>
              {showError && errorMessage && (
                <VDBText
                  color={theme["error-01"]}
                  type={typography["Minor-R"]}
                  style={{ marginTop: 4 }}
                >
                  {errorMessage}
                </VDBText>
              )}
            </>
          ) : (
            <View
              ref={triggerRef}
              onLayout={(event) => {
                setLayout(
                  event?.nativeEvent?.layout as unknown as SetStateAction<null>
                );
              }}
            >
              <Controller
                control={control}
                name="searchQueryFormValue"
                render={({ field: { onChange, onBlur, value } }) => (
                  <Wrapper
                    onPress={
                      !theme.isLargeScreen ? openFilterSearchScreen : undefined
                    }
                  >
                    <Input
                      inputLabel={t(field?.label || "")}
                      {...(!theme.isLargeScreen && {
                        disabled: true,
                        ignoreDisabledStyle: true,
                        pointerEvents: "none",
                      })}
                      onBlur={() => {
                        if (!isDropdownVisible) {
                          if (
                            dynamicFormValues.requestForm[field.name] &&
                            dynamicFormValues.requestForm[field.name] !==
                              value &&
                            shareType !== "share_via_whatsapp"
                          ) {
                            showModal?.(field.name, () => {
                              setValue("searchQueryFormValue", value);
                              onSelect(value);
                            });
                          } else {
                            setValue("searchQueryFormValue", value);
                            onSelect(value);
                          }
                        } else if ((apiData as any)?.length < 1) {
                          setValue("searchQueryFormValue", value);
                          onSelect(value);
                        } else {
                          setValue("searchQueryFormValue", value);
                          onSelect(value);
                        }
                        setTimeout(() => {
                          setDropdownVisible(false);
                        }, 200);
                        onBlur();
                      }}
                      onChangeText={(text) => {
                        onChange(text);
                        setSearchQuery(text);
                      }}
                      value={value}
                      error={error || ""}
                    />
                  </Wrapper>
                )}
              />
            </View>
          )}
        </>
      }
      <View style={{ flex: 1 }}>
        {isDropdownVisible && dropdownPosition && (
          <DropdownContainer
            position={dropdownPosition}
            theme={theme}
            maxHeight={Dimensions.get("window").height * 0.4}
            id="dropdown"
          >
            <FlatList
              data={apiData || options}
              keyExtractor={(item, index) => `${item.value}-${index}`}
              keyboardShouldPersistTaps="handled"
              nestedScrollEnabled={true}
              renderItem={({ item, index }) => {
                const fontFamily =
                  isFontStyleEnabled && item.value
                    ? getFontFamilyForStyle(item.value)
                    : undefined;

                return (
                  <View style={{ pointerEvents: "box-none" }}>
                    <OptionButton
                      key={index}
                      onPress={() => {
                        handleSelect(item.value, item, true);
                      }}
                      isSelected={item.value === selectedValue}
                      isDisabled={item.isDisabled || false}
                      theme={theme}
                    >
                      <View>
                        <VDBText
                          color={theme["core-05"]}
                          type={
                            isFontStyleEnabled && fontFamily
                              ? {
                                  ...typography["Body-M"],
                                  fontFamily,
                                }
                              : typography["Body-M"]
                          }
                        >
                          {item.label}
                        </VDBText>
                        <Spacer size={SpaceType.Basic} />
                        {(item as any)?.description && (
                          <VDBText
                            color={theme["core-06"]}
                            type={typography["Minor-R"]}
                          >
                            {(item as any)?.description}
                          </VDBText>
                        )}
                      </View>
                    </OptionButton>
                    {index <
                      (field?.api ? (apiData as any)?.length : options.length) -
                        1 && <LineViewContainer background="core-02" />}
                  </View>
                );
              }}
            />
          </DropdownContainer>
        )}
      </View>
    </Container>
  );
};

const Container = styled.View``;

const TriggerButton = styled.TouchableOpacity<{
  theme: any;
  disabled?: boolean;
  hasError?: boolean;
}>`
  height: auto;
  padding: 10px;
  border-radius: 12px;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-color: ${(props: {
    theme: DefaultTheme;
    disabled?: boolean;
    hasError?: boolean;
  }) =>
    props.hasError
      ? props.theme["error-01"]
      : props.disabled
      ? props.theme["core-03"]
      : props.theme["core-06"]};
  border-width: 1px;
  background-color: ${({
    theme,
    disabled,
  }: {
    theme: DefaultTheme;
    disabled?: boolean;
  }) => (disabled ? theme["core-02"] : theme["core-01"])};
`;

const DropdownContainer = styled.View<{
  position: { top: number; left: number; width: number };
  maxHeight: number;
}>`
  position: ${(theme: { isMobile: boolean; isTablet: boolean }) =>
    theme.isMobile || theme.isTablet ? "absolute" : "relative"};
  width: ${(props: { position: { width: number } }) => props.position.width}px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-radius: 10px;
  elevation: 10;
  max-height: ${(props: { maxHeight: number }) => props.maxHeight}px;
  overflow: hidden;
  z-index: 1000;
  box-shadow: 0px 2px 3.84px rgba(0, 0, 0, 0.3);
`;

const TouchableContainer = styled.Pressable<{}>`
  border-radius: ${SpaceType.Basic}px;
`;

const OptionButton = styled.Pressable<{
  isSelected: boolean;
  isDisabled: boolean;
  theme: any;
}>`
  padding: 8px;
  background-color: ${(props: {
    isSelected: boolean;
    isDisabled: boolean;
    theme: DefaultTheme;
  }) => (props.isSelected ? props.theme["core-02"] : "transparent")};
  opacity: ${(props: { isDisabled: boolean }) => (props.isDisabled ? 0.5 : 1)};
  z-index: 1;
  elevation: 1;
`;

export default CustomSelect;
